import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocale } from '@/i18n/locale-context';
import { createEmbedIframe } from '@/lib/dom';
import { Modal, cn } from '@/components/ui';

/**
 * The article body — PRD §14, phases.md §1.5.
 *
 * `content_html` was sanitized **on write** (Phase 5) and is served verbatim; §1.5 is
 * explicit that it is never sanitized on read, so this renders it with
 * `dangerouslySetInnerHTML` rather than re-parsing it into React elements. That is the
 * cheapest correct thing to do, and it keeps the server-rendered markup byte-identical
 * to what hydration produces.
 *
 * Two behaviours the task list asks for cannot come from static HTML, and both are added
 * without a parser, by transforming the string and then delegating events:
 *
 * - **Embeds are facade-loaded.** A YouTube `<iframe>` in the markup pulls ~1 MB of
 *   player script into a page a reader may never scroll to. The transform below replaces
 *   it with a poster and a play button; the real iframe is inserted on click. The
 *   facade is server-rendered, so it costs no JavaScript to *appear*.
 * - **Images open in a lightbox.** One delegated click handler on the container, rather
 *   than a component per image — the images arrive as HTML, so there is no component to
 *   attach to.
 */

const YOUTUBE_ID =
  /(?:(?:m\.|www\.)?youtube(?:-nocookie)?\.com\/(?:embed\/|watch\?v=|v\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/;

/** Rewrites embed iframes into facades. Pure, and run identically on server and client. */
export function toFacades(html: string): string {
  return html.replace(
    /<div class="embed embed--(\w+)([^"]*)"([^>]*)><iframe([^>]*)><\/iframe><\/div>/g,
    (whole, provider: string, extraClasses: string, wrapperAttrs: string, iframeAttrs: string) => {
      const src = /src="([^"]+)"/.exec(iframeAttrs)?.[1];
      if (!src) return whole;

      const title = /title="([^"]*)"/.exec(iframeAttrs)?.[1] ?? '';
      const videoId = provider === 'youtube' ? YOUTUBE_ID.exec(src)?.[1] : undefined;
      const poster = videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : '';

      return (
        `<div class="embed embed--${provider}${extraClasses} nk-embed-facade"${wrapperAttrs}` +
        ` data-embed-src="${src}" data-embed-title="${title}">` +
        (poster
          ? `<img src="${poster}" alt="" loading="lazy" decoding="async" class="nk-embed-poster" />`
          : '') +
        `<button type="button" class="nk-embed-play" data-embed-play aria-label="${title || 'Play'}">` +
        '<span aria-hidden="true">▶</span>' +
        '</button>' +
        '</div>'
      );
    },
  );
}

export function ArticleBody({ html, className }: { html: string; className?: string }) {
  const { t } = useLocale();
  const container = useRef<HTMLDivElement>(null);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);

  const onClick = useCallback((event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    const play = target.closest<HTMLElement>('[data-embed-play]');
    if (play) {
      const facade = play.closest<HTMLElement>('.nk-embed-facade');
      const src = facade?.dataset['embedSrc'];
      if (!facade || !src) return;
      event.preventDefault();

      facade.replaceChildren(createEmbedIframe(src, facade.dataset['embedTitle'] ?? ''));
      facade.classList.remove('nk-embed-facade');
      return;
    }

    const image = target.closest<HTMLImageElement>('img:not(.nk-embed-poster)');
    if (image?.src) {
      event.preventDefault();
      setLightbox({ src: image.src, alt: image.alt });
    }
  }, []);

  useEffect(() => {
    const node = container.current;
    if (!node) return undefined;
    node.addEventListener('click', onClick);
    return () => node.removeEventListener('click', onClick);
  }, [onClick]);

  return (
    <>
      <div
        ref={container}
        className={cn('nk-prose', className)}
        // Sanitized on write (§1.5). Never sanitized here, and never assembled here.
        dangerouslySetInnerHTML={{ __html: toFacades(html) }}
      />
      <Modal
        open={lightbox !== null}
        onClose={() => setLightbox(null)}
        title={lightbox?.alt || t('action.viewAll')}
        className="max-w-4xl"
      >
        {lightbox && (
          <img src={lightbox.src} alt={lightbox.alt} className="mx-auto max-h-[70vh] w-auto" />
        )}
      </Modal>
    </>
  );
}
